import { Building2, ChevronRight, FileText } from 'lucide-react';
import type { Exhibitor, Paper, Profile } from '@conference/shared';
import { apiAssetUrl } from '../../services/apiClient';
import { AvatarImage } from '../../components/AvatarImage';

type FeaturedKind = 'featured_speakers' | 'featured_papers' | 'featured_exhibitors';
type Item = Profile | Paper | Exhibitor;

function details(kind: FeaturedKind, item: Item): string[] {
  if (kind === 'featured_speakers') {
    const speaker = item as Profile;
    return [speaker.title, speaker.company].filter(Boolean) as string[];
  }
  if (kind === 'featured_papers') {
    const paper = item as Paper;
    return [paper.authors.join(', '), paper.presentation_topic ?? paper.session?.title].filter(Boolean) as string[];
  }
  const exhibitor = item as Exhibitor;
  return [exhibitor.category, exhibitor.booth ? `Booth ${exhibitor.booth}` : exhibitor.tier].filter(Boolean) as string[];
}

function FeaturedVisual({ kind, item }: { kind: FeaturedKind; item: Item }) {
  if (kind === 'featured_speakers') {
    const speaker = item as Profile;
    return <div className="size-11 shrink-0 overflow-hidden rounded-full border flex items-center justify-center" style={{ background: 'var(--app-accent-dim)', borderColor: 'var(--app-accent-border)', color: 'var(--app-accent)' }}>
      <AvatarImage source={speaker.avatar_url} alt="" className="size-full object-cover" fallback={<span className="text-sm font-semibold">{speaker.name.charAt(0)}</span>} />
    </div>;
  }
  if (kind === 'featured_exhibitors') {
    const exhibitor = item as Exhibitor;
    return <div className="size-11 shrink-0 overflow-hidden rounded-lg border flex items-center justify-center p-1.5" style={{ background: '#fff', borderColor: 'var(--app-border)' }}>
      {exhibitor.logo_url
        ? <img src={apiAssetUrl(exhibitor.logo_url) ?? undefined} alt="" className="size-full object-contain" />
        : <Building2 className="size-5" style={{ color: 'var(--app-accent)' }} />}
    </div>;
  }
  return <div className="size-11 shrink-0 rounded-lg flex items-center justify-center" style={{ background: 'var(--app-accent-dim)' }}>
    <FileText className="size-5" style={{ color: 'var(--app-accent)' }} />
  </div>;
}

export function HomeFeaturedCollection({ kind, title, items, limit, onNavigate, onSeeAll }: { kind: FeaturedKind; title: string; items: Item[]; limit: number; onNavigate: (path: string) => void; onSeeAll?: () => void }) {
  const displayed = items.slice(0, limit);
  if (!displayed.length) return null;
  const pathFor = (item: Item) => kind === 'featured_speakers' ? `/speaker/${item.id}` : kind === 'featured_papers' ? `/paper/${item.id}` : `/exhibitor/${item.id}`;
  const textFor = (item: Item) => 'name' in item ? item.name : item.title;
  return <section>
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className="text-lg font-semibold tracking-tight" style={{ color: 'var(--app-text)' }}>{title}</h3>
      {onSeeAll && <button onClick={onSeeAll} className="text-xs font-medium active:opacity-70" style={{ color: 'var(--app-accent)' }}>See all</button>}
    </div>
    <ul aria-label={title} className="space-y-2">
      {displayed.map((item) => {
        const [metadata, context] = details(kind, item);
        return <li key={item.id}>
          <button
            aria-label={textFor(item)}
            onClick={() => onNavigate(pathFor(item))}
            className="w-full rounded-xl border p-3.5 flex items-center gap-3 text-left active:scale-[0.98] transition-transform"
            style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)', boxShadow: 'var(--app-card-shadow)' }}
          >
            <FeaturedVisual kind={kind} item={item} />
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-semibold leading-snug line-clamp-2" style={{ color: 'var(--app-text)' }}>{textFor(item)}</span>
              {metadata && <span className="mt-1 block text-xs leading-relaxed line-clamp-1" style={{ color: 'var(--app-text-muted)' }}>{metadata}</span>}
              {context && <span className="block text-[11px] leading-relaxed line-clamp-1" style={{ color: 'var(--app-text-subtle)' }}>{context}</span>}
            </span>
            <ChevronRight className="size-4 shrink-0" style={{ color: 'var(--app-text-subtle)' }} />
          </button>
        </li>;
      })}
    </ul>
  </section>;
}
